import { Download, File, FileText, Image, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface AttachmentData {
  id?: string;
  name: string;
  url: string;
  type: string;
  size: number;
}

interface MessageAttachmentDisplayProps {
  attachments: AttachmentData[];
  className?: string;
}

export const MessageAttachmentDisplay = ({
  attachments,
  className,
}: MessageAttachmentDisplayProps) => {
  if (!attachments || attachments.length === 0) return null;

  const getFileIcon = (type: string) => {
    if (type.startsWith("image/")) return <Image className="h-4 w-4" />;
    if (type === "application/pdf") return <FileText className="h-4 w-4" />;
    return <File className="h-4 w-4" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isImage = (type: string) => type.startsWith("image/");

  return (
    <div className={cn("mt-2 space-y-2", className)}>
      {attachments.map((attachment, index) => (
        <div key={attachment.id || index}>
          {isImage(attachment.type) ? (
            // Image preview
            <a
              href={attachment.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block group relative"
            >
              <img
                src={attachment.url}
                alt={attachment.name}
                className="max-w-[300px] max-h-[200px] rounded-lg object-cover border"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg flex items-center justify-center">
                <ExternalLink className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </a>
          ) : (
            // File preview
            <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 border max-w-[300px]">
              {getFileIcon(attachment.type)}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{attachment.name}</p>
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(attachment.size)}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                asChild
              >
                <a
                  href={attachment.url}
                  download={attachment.name}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Download className="h-4 w-4" />
                </a>
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
